/**
 * Composes the administrative settings for one paired Workspace Vault.
 *
 * Pairing and automation share a workspace boundary but have distinct
 * lifecycle consequences. Connector credentials and Vault grants live at
 * their own product boundaries rather than being duplicated here.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Database, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { useServices } from '@/app-shell/services'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  Switch,
} from '@/components/ui'
import {
  disableWorkspaceVault,
  enableWorkspaceVault,
  getWorkspaceVaultPairing,
  listVaults,
} from '../services/vault-api'

export function WorkspaceVaultSettings({
  orgId,
  workspaceId,
  workspaceName,
  canManage,
}: {
  orgId: string
  workspaceId: string
  workspaceName: string
  canManage: boolean
}) {
  const api = useServices().getClient()
  const queryClient = useQueryClient()
  const catalogKey = ['vault-catalog', orgId]
  const pairingKey = ['vault-pairing', orgId, workspaceId]
  const pairing = useQuery({
    queryKey: pairingKey,
    queryFn: () => getWorkspaceVaultPairing(api, orgId, workspaceId),
  })
  const catalog = useQuery({
    queryKey: catalogKey,
    queryFn: () => listVaults(api, orgId),
  })
  const current = pairing.data
  const enabled = current?.enabled ?? false
  const vault = catalog.data?.find(
    entry => entry.vault.vaultId === current?.vaultId,
  )?.vault

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: catalogKey }),
      queryClient.invalidateQueries({ queryKey: pairingKey }),
      queryClient.invalidateQueries({ queryKey: ['vault-items'] }),
    ])
  }
  const toggle = useMutation({
    mutationFn: (checked: boolean) => checked
      ? enableWorkspaceVault(
          api,
          orgId,
          workspaceId,
          `${workspaceName} Vault`,
        )
      : disableWorkspaceVault(api, orgId, workspaceId),
    onSuccess: async updated => {
      queryClient.setQueryData(pairingKey, updated)
      await invalidate()
      toast.success(updated.enabled ? 'Workspace Vault enabled' : 'Workspace Vault disabled')
    },
    onError: (error: any) => toast.error(
      error?.response?.data?.detail ?? 'Workspace Vault setting could not be saved',
    ),
  })
  if (pairing.isPending || catalog.isPending) {
    return <LoadingSettings />
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Vault</CardTitle>
        <CardDescription>
          Give this workspace a shared Vault. When enabled, incoming documents
          appear in Vault automatically as live, read-only folders.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between gap-5 rounded-xl border p-4">
          <div className="space-y-1">
            <Label htmlFor="workspace-vault-enabled" className="text-sm font-semibold">
              Enable Vault for this workspace
            </Label>
            <p className="max-w-2xl text-xs leading-5 text-muted-foreground">
              Off by default for new workspaces. Turning it on creates this
              workspace&apos;s Vault; turning it off hides the Vault without deleting anything.
            </p>
          </div>
          <Switch
            id="workspace-vault-enabled"
            checked={enabled}
            disabled={!canManage || toggle.isPending}
            onCheckedChange={checked => toggle.mutate(checked)}
          />
        </div>

        {enabled && vault && current && (
          <section className="space-y-3 border-t pt-5">
            <div>
              <h3 className="text-sm font-semibold">This workspace in Vault</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                One Vault is created automatically and remains scoped to this workspace.
                Files from connected sources enter the configured review workflow automatically.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-muted/20 p-4">
              <div className="flex min-w-0 items-center gap-3">
                <span className="rounded-lg bg-indigo-100 p-2.5 text-indigo-700">
                  <Database className="h-4 w-4" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{vault.name}</p>
                  <p className="font-mono text-xs text-muted-foreground">
                    {workspaceId} · Incoming — live, read-only
                  </p>
                </div>
              </div>
              <a
                className="text-sm font-medium text-primary hover:underline"
                href={`#/vault?org=${encodeURIComponent(orgId)}&ws=${encodeURIComponent(workspaceId)}&vault=${encodeURIComponent(vault.vaultId)}`}
              >
                Open in Vault
              </a>
            </div>
          </section>
        )}
        {enabled && (!vault || !current) && <LoadingInline />}
      </CardContent>
    </Card>
  )
}

function LoadingSettings() {
  return (
    <Card>
      <CardContent className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading Vault settings…
      </CardContent>
    </Card>
  )
}

function LoadingInline() {
  return (
    <p className="flex items-center gap-2 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> Preparing Workspace Vault…
    </p>
  )
}
